// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
export type Order = 'asc' | 'desc';
export type NumericPriority = 'first' | 'last' | 'none';

export interface KeyComparatorOptions {
  order?: Order;
  numericPriority?: NumericPriority;
  locale?: string;
  localeOptions?: Intl.CollatorOptions;
}

export function createKeyComparator<T = unknown>({
  order = 'asc',
  numericPriority = 'first',
  locale = 'en',
  localeOptions = {
    numeric: true,
    sensitivity: 'base',
  },
}: KeyComparatorOptions = {}) {
  const sign = order === 'asc' ? 1 : -1;
  return function compareEntries(
    [ka]: [string, T],
    [kb]: [string, T],
  ): number {
    const na = Number(ka);
    const nb = Number(kb);
    const aIsNum = Number.isFinite(na);
    const bIsNum = Number.isFinite(nb);
    if (numericPriority !== 'none' && aIsNum !== bIsNum) {
      if (numericPriority === 'first') {
        return aIsNum ? -1 : 1;
      }
      if (numericPriority === 'last') {
        return aIsNum ? 1 : -1;
      }
    }
    const r = ka.localeCompare(kb, locale, localeOptions);
    return sign * r;
  }
}

export const compareKeysNumericFirstAsc = createKeyComparator({
  order: 'asc', numericPriority: 'first',
});
export const compareKeysNumericFirstDesc = createKeyComparator({
  order: 'desc', numericPriority: 'first',
});

export const KeyComparatorFactory = {
  createKeyComparator,
  compareKeysNumericFirstAsc,
  compareKeysNumericFirstDesc,
} as const;
